import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useHasEnteredViewport } from '../../../../hooks/dom';

interface LazyRendererNodeProps extends RouteComponentProps<any> {
    initialRender?: boolean;
}

const LazyRendererNode = withRouter(
    (props: React.PropsWithChildren<LazyRendererNodeProps>) => {
        const ref = React.useRef(null);
        const { hasEnteredViewport, reset } = useHasEnteredViewport(ref);
        const shouldRender = hasEnteredViewport || props.initialRender;

        React.useEffect(() => {
            const unlisten = props.history.listen(() => {
                reset();
            });

            return () => unlisten();
        }, [props.history, reset]);

        return (
            <div
                className="lazy-render-proxy"
                ref={ref}
                style={{
                    height: shouldRender ? 'auto' : '400px',
                }}
            >
                {shouldRender ? props.children : null}
            </div>
        );
    },
);

interface LazyRendererProps {
    /**
     * Zero indexed number representing
     * number of children to initially
     * render.
     *
     * If set to -1 will render all
     * children synchronously
     */
    numChildInitialRender?: number;
}

export const LazyRenderer: React.FunctionComponent<LazyRendererProps> = (
    props,
) => {
    const { numChildInitialRender = 0 } = props;

    if (!props.children) {
        return null;
    }

    return (
        <>
            {React.Children.map(props.children, (child, index) => {
                return (
                    <LazyRendererNode
                        initialRender={
                            numChildInitialRender === -1
                                ? true
                                : index <= numChildInitialRender
                        }
                    >
                        {child}
                    </LazyRendererNode>
                );
            })}
        </>
    );
};
